/** API */
import { api } from '.';
import { EpisodePurchaseType, EpisodeStatus } from '../constants';
import {
  IEpisode,
  IEpisodeDetail,
  IKeyPagination,
  IMyEpisode,
  IMyEpisodeDetail,
  IPagination,
  ISavePoint,
  ISimpleUser,
  IStory,
  IStoryDetail,
} from '../model';
import { toQueryString } from '../utils/misc';

export type PostMyEpisodePayload = {
  title: string;
  rewriteTargetId?: number;
};

export const postMyEpisode = async (
  storyId: number,
  payload: PostMyEpisodePayload
) => {
  const { data } = await api.post<IMyEpisodeDetail>(
    `/my-stories/${storyId}/episodes/`,
    {
      status: EpisodeStatus.private,
      ...payload,
    }
  );
  return data;
};

export const deleteMyEpisode = async (storyId: number, episodeId: number) => {
  const { data } = await api.delete(
    `/my-stories/${storyId}/episodes/${episodeId}/`
  );
  return data;
};

export const deleteEpisodeBooking = async (
  storyId: number,
  episodeId: number
) => {
  const { data } = await api.delete(
    `/my-stories/${storyId}/episodes/${episodeId}/booking/`
  );
  return data;
};

export type PatchMyEpisodePayload = Partial<IMyEpisodeDetail> & {
  isRenewForContent?: boolean;
  featuredCharactersIds?: number[];
};

export const patchMyEpisode = async (
  storyId: number,
  episodeId: number,
  payload: PatchMyEpisodePayload
) => {
  const { data } = await api.patch<IMyEpisodeDetail>(
    `/my-stories/${storyId}/episodes/${episodeId}/`,
    payload
  );
  return data;
};

export const fetchMyEpisodes = async (
  storyId: number,
  status: 'all' | 'published' | 'public',
  ordering:
    | 'newest'
    | 'oldest'
    | 'pubest'
    | 'published_oldest'
    | 'published_newest'
    | 'created_newest'
    | 'created_oldest',
  page = 1,
  perPage = 20
) => {
  const { data } = await api.get<IPagination<IMyEpisode>>(
    `/my-stories/${storyId}/episodes/${toQueryString({
      status,
      ordering,
      page,
      perPage,
    })}`
  );
  return data;
};

export const fetchMyEpisode = async (storyId: number, episodeId: number) => {
  const { data } = await api.get<IMyEpisodeDetail>(
    `/my-stories/${storyId}/episodes/${episodeId}/`
  );

  if (data.story?.version === 2) {
    return { ...data, story: { ...data.story, isV2Story: true } };
  }

  return data;
};

export const fetchEpisodes = async (
  storyId: number,
  ordering: 'newest' | 'oldest',
  page = 1,
  perPage = 20
) => {
  const { data } = await api.get<IPagination<IEpisode>>(
    `/stories/${storyId}/episodes/${toQueryString({ page, perPage, ordering })}`
  );
  return data;
};

export const fetchEpisode = async (storyId: number, episodeId: number) => {
  const { data } = await api.get<IEpisodeDetail>(
    `/stories/${storyId}/episodes/${episodeId}/`
  );
  return data;
};

export type PurchaseEpisodeByBalloonPayload = {
  storyId: number;
  episodeId: number;
  voucherId?: number;
  isAutoApplyVoucher?: boolean;
  type: EpisodePurchaseType;
};

interface PurchaseEpisodeByBalloonHistory {
  id: number;
  story: Pick<IStoryDetail, 'id' | 'title'>;
  episode: Pick<IEpisode, 'id' | 'title' | 'purchaseType' | 'purchaseExpired'>;
}

export const purchaseEpisodeByBalloon = async ({
  storyId,
  episodeId,
  voucherId,
  isAutoApplyVoucher,
  type,
}: PurchaseEpisodeByBalloonPayload) => {
  const { data } = await api.post<PurchaseEpisodeByBalloonHistory>(
    `/stories/${storyId}/episodes/${episodeId}/purchase-by-balloon/`,
    { type, voucherId, isAutoApplyVoucher }
  );
  return data;
};

export interface PreviewEpisodeByBalloonPayload {
  storyId: number;
  episodeId: number;
  balloon: number;
  message?: string;
  voucherId?: number;
  useStoryPass?: boolean;
}

export const previewEpisodeByBalloon = async ({
  storyId,
  episodeId,
  balloon,
  message,
  voucherId,
  useStoryPass = false,
}: PreviewEpisodeByBalloonPayload) => {
  const { data } = await api.post(
    `/stories/${storyId}/episodes/${episodeId}/preview-by-balloon/`,
    {
      balloon,
      message,
      voucherId,
      useStoryPass,
    }
  );
  return data;
};

export type EpisodeReadDataPayload = {
  tapCount: number;
  contentType: 'S' | 'C';
  readType: 'I' | 'C';
  readStart: ISavePoint;
  readEnd: ISavePoint;
  readProgress: number;
  readTime: number;
  source?: string;
  adId?: number;
};

export const postEpisodeRead = async (
  storyId: number,
  episodeId: number,
  readData: EpisodeReadDataPayload
) => {
  const { data } = await api.post(
    `/stories/${storyId}/episodes/${episodeId}/read/`,
    readData
  );
  return data;
};

/**
 * 내 작품 회차
 */

export enum PushType {
  default = 'DF',
  custom = 'CUS',
}

export type EpisodeBookingPayload = {
  booked: Date;
  pushType: PushType;
  pushBody?: string;
};

export interface IEpisodeBooking {
  id: number;
  episodeId: number;
  story: Pick<IStory, 'id' | 'title'>;
  pushBody?: string;
  pushType: PushType;
}

export const postEpisodeBooking = async (
  storyId: number,
  episodeId: number,
  payload: EpisodeBookingPayload
) => {
  const newPayload = {
    ...payload,
    booked: payload.booked.toISOString(),
    pushBody:
      payload.pushType === PushType.custom ? payload.pushBody : undefined,
  };

  const { data } = await api.post<IEpisodeBooking>(
    `/my-stories/${storyId}/episodes/${episodeId}/booking/`,
    newPayload
  );
  return data;
};

export type IEpisodeStatistic = {
  readerCount: number;
  commentedReaderCount: number;
  donatedBalloon: number;
  donatedReaderCount: number;
  followedReaderCount: number;
  readEventChart?: number[];
  completedReaderCount: number;
  completedReadTime: number;
  status: 'RDY' | 'INIT' | 'PROG' | 'DONE' | 'FAIL';
  created: string;
  modified: string;
  story: Pick<IStory, 'id' | 'title' | 'coverImage'>;
  episode: Pick<IEpisode, 'id' | 'title' | 'published'>;
};

export const fetchEpisodeStatistic = async (
  storyId: number,
  episodeId: number
) => {
  const { data } = await api.get<IEpisodeStatistic>(
    `/my-stories/${storyId}/episodes/${episodeId}/statistic/`
  );
  return data;
};

export const postEpisodeStatistic = async ({
  storyId,
  episodeId,
}: {
  storyId: number;
  episodeId: number;
}) => {
  const { data } = await api.post(
    `/my-stories/${storyId}/episodes/${episodeId}/statistic/`
  );
  return data;
};

export const fetchEpisodeStatisticReaders = async (
  storyId: number,
  episodeId: number,
  lastEvaluatedKey = ''
) => {
  const { data } = await api.get<IKeyPagination<ISimpleUser>>(
    `/my-stories/${storyId}/episodes/${episodeId}/statistic/readers/${toQueryString(
      { lastEvaluatedKey }
    )}`
  );
  return data;
};

export interface ILatestPublicEpisodeReaderInfo {
  readerCount: number;
}
